/**
 * 页面组件映射表
 * 将后端返回的 file_path 映射到实际的组件
 */
import { lazy, type ComponentType } from 'react'

type LayoutAwareComponent = ComponentType & {
  hideBreadcrumbTitle?: boolean
  hideGlobalBreadcrumb?: boolean
}

const lazyPage = (
  loader: () => Promise<{ default: ComponentType<any> }>,
  layout: Pick<LayoutAwareComponent, 'hideBreadcrumbTitle' | 'hideGlobalBreadcrumb'> = {}
): LayoutAwareComponent => Object.assign(lazy(loader) as unknown as LayoutAwareComponent, layout)

const lazyCrudPage = (loader: () => Promise<{ default: ComponentType<any> }>): LayoutAwareComponent =>
  lazyPage(loader, { hideBreadcrumbTitle: true })

// 懒加载所有页面组件
const componentMap: Record<string, LayoutAwareComponent> = {
  // ==================== 认证 ====================
  'pages/login/LoginPage': lazy(() => import('@/pages/login/LoginPage')),

  // ==================== 首页 ====================
  'pages/home/HomePage': lazy(() => import('@/pages/home/HomePage')),
  'pages/dashboard/DataDashboardPage': lazyPage(() => import('@/pages/dashboard/DataDashboardPage'), {
    hideGlobalBreadcrumb: true,
  }),
  'pages/map/overview/MapOverviewPage': lazy(() => import('@/pages/map/overview/MapOverviewPage')),
  'pages/map/disease-assets/MapDiseaseAssetsPage': lazyPage(() => import('@/pages/map/disease-assets/MapDiseaseAssetsPage'), {
    hideGlobalBreadcrumb: true,
  }),
  'pages/map/jump/MapJumpPage': lazyPage(() => import('@/pages/map/jump/MapJumpPage'), {
    hideGlobalBreadcrumb: true,
  }),
  'pages/map/rutting/MapRuttingPage': lazyPage(() => import('@/pages/map/rutting/MapRuttingPage'), {
    hideGlobalBreadcrumb: true,
  }),
  'pages/map/traffic-volume/MapTrafficVolumePage': lazyPage(() => import('@/pages/map/traffic-volume/MapTrafficVolumePage'), {
    hideGlobalBreadcrumb: true,
  }),
  'pages/map/fwd/MapFwdPage': lazyPage(() => import('@/pages/map/fwd/MapFwdPage'), {
    hideGlobalBreadcrumb: true,
  }),
  'pages/map/draw/MapDrawPage': lazy(() => import('@/pages/map/draw/MapDrawPage')),
  'pages/map/patrol-v2/MapPatrolV2Page': lazyPage(() => import('@/pages/map/patrol-v2/MapPatrolV2Page'), {
    hideGlobalBreadcrumb: true,
  }),
  'pages/map/patrol-board/MapPatrolBoardPage': lazyPage(() => import('@/pages/map/patrol-board/MapPatrolBoardPage'), {
    hideGlobalBreadcrumb: true,
  }),

  // ==================== 基础数据 - 道路管理 ====================
  'pages/basic/road/static/StaticIndexPage': lazy(() => import('@/pages/basic/road/static/StaticIndexPage')),
  'pages/basic/road/static/RoadTreePage': lazy(() => import('@/pages/basic/road/static/RoadTreePage')),
  'pages/basic/road/static/StructurePage': lazyCrudPage(() => import('@/pages/basic/road/static/StructurePage')),
  'pages/basic/road/static/GeometryPage': lazyCrudPage(() => import('@/pages/basic/road/static/GeometryPage')),
  'pages/basic/road/static/TrafficPage': lazyCrudPage(() => import('@/pages/basic/road/static/TrafficPage')),
  'pages/basic/road/static/TrafficLaneLoadPage': lazyCrudPage(() => import('@/pages/basic/road/static/TrafficLaneLoadPage')),
  'pages/basic/road/static/LanePage': lazyCrudPage(() => import('@/pages/basic/road/static/LanePage')),
  'pages/basic/road/static/HectometerPage': lazyCrudPage(() => import('@/pages/basic/road/static/HectometerPage')),
  'pages/basic/road/static/RoadLifePage': lazyCrudPage(() => import('@/pages/basic/road/static/RoadLifePage')),
  'pages/basic/road/static/TangentWeightConfigPage': lazyCrudPage(() => import('@/pages/basic/road/static/TangentWeightConfigPage')),
  'pages/basic/road/static/TangentScenePage': lazyCrudPage(() => import('@/pages/basic/road/static/TangentScenePage')),
  'pages/basic/road/static/HistoryPage': lazyCrudPage(() => import('@/pages/basic/road/static/HistoryPage')),
  'pages/basic/road/static/RoadPage': lazy(() => import('@/pages/basic/road/static/RoadPage')),
  'pages/basic/road/inspect/InspectPage': lazyCrudPage(() => import('@/pages/basic/road/inspect/InspectPage')),
  'pages/basic/road/sub/QueryPage': lazyCrudPage(() => import('@/pages/basic/road/sub/QueryPage')),
  'pages/basic/road/tangent/TangentPage': lazyCrudPage(() => import('@/pages/basic/road/tangent/TangentPage')),

  // ==================== 基础数据 - 历史检测数据 ====================
  'pages/basic/road/history/HistoryIndexPage': lazy(() => import('@/pages/basic/road/history/HistoryIndexPage')),
  'pages/basic/road/history/FwdDataPage': lazyCrudPage(() => import('@/pages/basic/road/history/FwdDataPage')),
  'pages/basic/road/history/GprDataPage': lazyCrudPage(() => import('@/pages/basic/road/history/GprDataPage')),
  'pages/basic/road/history/LicsDataPage': lazyCrudPage(() => import('@/pages/basic/road/history/LicsDataPage')),
  'pages/basic/road/history/IriDataPage': lazyCrudPage(() => import('@/pages/basic/road/history/IriDataPage')),
  'pages/basic/road/history/Iri20mSummaryPage': lazyCrudPage(() => import('@/pages/basic/road/history/Iri20mSummaryPage')),
  'pages/basic/road/history/DiseaseScanPage': lazyCrudPage(() => import('@/pages/basic/road/history/DiseaseScanPage')),
  'pages/basic/road/history/AbnormalJumpsRawPage': lazyCrudPage(() => import('@/pages/basic/road/history/AbnormalJumpsRawPage')),
  'pages/basic/road/history/AbnormalJumpsStatPage': lazyCrudPage(() => import('@/pages/basic/road/history/AbnormalJumpsStatPage')),
  'pages/basic/road/history/jump-stat': lazyCrudPage(() => import('@/pages/basic/road/history/AbnormalJumpsStatPage')),

  // ==================== 基础数据 - 道路查询 ====================
  'pages/basic/road/query/RoadQueryPage': lazyCrudPage(() => import('@/pages/basic/road/query/RoadQueryPage')),

  // ==================== 基础数据 - 道路指标 ====================
  'pages/basic/road/index/TangentIndexPage': lazyCrudPage(() => import('@/pages/basic/road/index/TangentIndexPage')),
  'pages/basic/road/index/TangentIndexFluctuationPage': lazyCrudPage(() => import('@/pages/basic/road/index/TangentIndexFluctuationPage')),

  // ==================== 工程管理 ====================
  'pages/basic/engineering/maintenance/MaintenancePage': lazyCrudPage(() => import('@/pages/basic/engineering/maintenance/MaintenancePage')),
  'pages/basic/engineering/method/MaintenanceMethodPage': lazyCrudPage(() => import('@/pages/basic/engineering/method/MaintenanceMethodPage')),

  // ==================== 模型管理 ====================
  'pages/model/index/PerformanceIndexPage': lazy(() => import('@/pages/model/index/PerformanceIndexPage')),
  'pages/model/optimization/BudgetOptimizationPage': lazy(() => import('@/pages/model/optimization/BudgetOptimizationPage')),
  'pages/model/tree/DecisionTreePage': lazy(() => import('@/pages/model/tree/DecisionTreePage')),
  'pages/model/tree/DecisionTreeManagementPage': lazyCrudPage(() => import('@/pages/model/tree/DecisionTreeManagementPage')),

  // ==================== 兼容后端错误路径 ====================
  'pages/modeltree/DecisionTreePage': lazy(() => import('@/pages/model/tree/DecisionTreePage')),

  // ==================== 科学决策 ====================
  'pages/decision/prediction/PerformancePredictionPage': lazy(() => import('@/pages/decision/prediction/PpiPredictionPage')),
  'pages/decision/prediction/PerformanceIndexPage': lazy(() => import('@/pages/decision/prediction/PpiPredictionIndexPage')),
  'pages/decision/prediction/PerformanceRecordPage': lazyCrudPage(() => import('@/pages/decision/prediction/PpiPredictionRecordPage')),
  'pages/decision/prediction/PpiPredictionPage': lazy(() => import('@/pages/decision/prediction/PpiPredictionPage')),
  'pages/decision/prediction/PpiPredictionIndexPage': lazy(() => import('@/pages/decision/prediction/PpiPredictionIndexPage')),
  'pages/decision/prediction/PpiPredictionRecordPage': lazyCrudPage(() => import('@/pages/decision/prediction/PpiPredictionRecordPage')),
  'pages/decision/maintenance/MaintenanceDecisionPage': lazy(() => import('@/pages/decision/maintenance/MaintenanceDecisionPage')),
  'pages/decision/maintenance/MaintenanceDecisionIndexPage': lazy(() => import('@/pages/decision/maintenance/MaintenanceDecisionIndexPage')),
  'pages/decision/maintenance/AutoMaintenancePlanPage': lazy(() => import('@/pages/decision/maintenance/AutoMaintenancePlanPage')),
  'pages/decision/maintenance/ResponsiveMaintenancePage': lazyPage(() => import('@/pages/decision/maintenance/ResponsiveMaintenancePage')),
  'pages/decision/maintenance/MaintenancePlanComparisonPage': lazyPage(() => import('@/pages/decision/maintenance/MaintenancePlanComparisonPage')),
  'pages/decision/maintenance/MaintenancePlanMapPlaybackPage': lazyPage(() => import('@/pages/decision/maintenance/MaintenancePlanMapPlaybackPage')),
  'pages/decision/maintenance/PpiPyramidPlanningPage': lazyPage(() => import('@/pages/decision/maintenance/PpiPyramidPlanningPage')),
  'pages/decision/maintenance/PpiPyramidComparisonPage': lazyPage(() => import('@/pages/decision/maintenance/PpiPyramidComparisonPage')),
  'pages/decision/maintenance/ResponsiveMaintenanceDetailPage': lazyCrudPage(() => import('@/pages/decision/maintenance/ResponsiveMaintenanceDetailPage')),
  'pages/decision/maintenance/MaintenanceRecordPage': lazyCrudPage(() => import('@/pages/decision/maintenance/MaintenanceRecordPage')),

  // ==================== 权限管理 ====================
  'pages/system/user/UserPage': lazyCrudPage(() => import('@/pages/system/user/UserPage')),
  'pages/system/role/RolePage': lazyCrudPage(() => import('@/pages/system/role/RolePage')),
  'pages/system/mapping/DiseaseMappingPage': lazyCrudPage(() => import('@/pages/system/mapping/DiseaseMappingPage')),
  'pages/system/config/ConfigOptionPage': lazyCrudPage(() => import('@/pages/system/config/ConfigOptionPage')),
  'pages/system/operationLog/OperationLogPage': lazyCrudPage(() => import('@/pages/system/operationLog/OperationLogPage')),

  // ==================== 测试页面 ====================
  'pages/test/TestPage': lazyCrudPage(() => import('@/pages/test/TestPage')),

  // ==================== 错误页 ====================
  'pages/error/NotFoundPage': lazy(() => import('@/pages/error/NotFoundPage')),
}

export default componentMap
